import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";
import { Loader2, FileText, CheckCircle2, XCircle, Download } from "lucide-react";

export default function MonthlyInvoiceDrafts() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [approving, setApproving] = useState(false);
  const [excludingId, setExcludingId] = useState(null);
  const [history, setHistory] = useState([]);

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/v2/monthly-invoices/drafts");
      setDrafts(res.data.items || []);
      const allRes = await api.get("/v2/monthly-invoices/drafts", { params: { status: "All" } });
      const hist = (allRes.data.items || [])
        .filter((d) => d.status === "APPROVED" || d.status === "GENERATION_FAILED")
        .sort((a, b) => new Date(b.approved_at || b.generation_failed_at || 0) - new Date(a.approved_at || a.generation_failed_at || 0))
        .slice(0, 20);
      setHistory(hist);
    } catch {
      toast.error("Failed to load drafts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === drafts.length) setSelected(new Set());
    else setSelected(new Set(drafts.map((d) => d.id)));
  };

  const handleBulkApprove = async () => {
    if (selected.size === 0) return;
    setApproving(true);
    try {
      const res = await api.post("/v2/monthly-invoices/drafts/bulk-approve", {
        draft_ids: Array.from(selected),
      });
      toast.success(`Approved ${res.data.approved_count} draft(s)`);
      setSelected(new Set());
      fetchDrafts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    } finally {
      setApproving(false);
    }
  };

  const handleExclude = async (id) => {
    const reason = window.prompt("Reason for excluding this draft (optional):") || "";
    setExcludingId(id);
    try {
      await api.post(`/v2/monthly-invoices/drafts/${id}/exclude`, { reason });
      toast.success("Draft excluded");
      fetchDrafts();
    } catch {
      toast.error("Failed to exclude");
    } finally {
      setExcludingId(null);
    }
  };

  const totalAmount = drafts.reduce((sum, d) => sum + (d.total_amount || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Monthly Invoice Drafts</h1>
          <p className="text-slate-500 text-sm">
            {drafts.length} pending drafts · ₹{totalAmount.toFixed(2)} total ·
            auto-generated on each customer's real billing cycle boundary (monthly for Wallet, Net-7/15/30 for Credit)
          </p>
        </div>

        {selected.size > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-slate-900 text-white rounded-xl shadow-2xl px-5 py-3 flex items-center gap-4">
            <span className="text-sm font-semibold">{selected.size} selected</span>
            <button
              onClick={handleBulkApprove}
              disabled={approving}
              className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50"
            >
              {approving ? "Approving..." : "Bulk Approve"}
            </button>
            <button onClick={() => setSelected(new Set())} className="text-slate-400 hover:text-white text-xs">
              Clear
            </button>
          </div>
        )}

        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : drafts.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500">No pending drafts right now.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="p-3 text-left">
                    <input type="checkbox" checked={selected.size === drafts.length} onChange={selectAll} className="w-4 h-4 accent-brand-600" />
                  </th>
                  <th className="p-3 text-left font-semibold text-slate-600">Customer</th>
                  <th className="p-3 text-left font-semibold text-slate-600">Cycle</th>
                  <th className="p-3 text-left font-semibold text-slate-600">Type</th>
                  <th className="p-3 text-right font-semibold text-slate-600">Shipments</th>
                  <th className="p-3 text-right font-semibold text-slate-600">Amount</th>
                  <th className="p-3 text-left font-semibold text-slate-600">Tax Split</th>
                  <th className="p-3 text-right font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {drafts.map((d) => (
                  <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-3">
                      <input type="checkbox" checked={selected.has(d.id)} onChange={() => toggleSelect(d.id)} className="w-4 h-4 accent-brand-600" />
                    </td>
                    <td className="p-3">
                      <div className="font-semibold text-slate-800">{d.customer_name}</div>
                      <div className="text-xs text-slate-400">{d.customer_email}</div>
                    </td>
                    <td className="p-3 text-slate-600">{d.cycle_start} → {d.cycle_end}</td>
                    <td className="p-3">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${d.account_mode === "CREDIT" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                        {d.account_mode === "CREDIT" ? "Credit" : "Wallet"}
                      </span>
                    </td>
                    <td className="p-3 text-right text-slate-700">{d.shipment_count}</td>
                    <td className="p-3 text-right font-semibold text-slate-800">₹{(d.total_amount || 0).toFixed(2)}</td>
                    <td className="p-3">
                      {d.tax_split_preview?.will_split_into_2 ? (
                        <span className="text-xs font-semibold text-amber-600" title={`${d.tax_split_preview.CGST_SGST_count} CGST+SGST, ${d.tax_split_preview.IGST_count} IGST`}>
                          ⚠️ Splits into 2 invoices
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">1 invoice</span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => handleExclude(d.id)}
                        disabled={excludingId === d.id}
                        title="Exclude this draft from the current batch"
                        className="text-xs text-red-500 hover:text-red-700 hover:underline disabled:opacity-50"
                      >
                        {excludingId === d.id ? "..." : "Exclude"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {history.length > 0 && (
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="p-3 border-b border-slate-200 bg-slate-50">
              <h2 className="text-sm font-bold text-slate-700 uppercase">Recent History</h2>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="p-3 text-left font-semibold text-slate-600">Customer</th>
                  <th className="p-3 text-left font-semibold text-slate-600">Cycle</th>
                  <th className="p-3 text-right font-semibold text-slate-600">Amount</th>
                  <th className="p-3 text-left font-semibold text-slate-600">Status</th>
                  <th className="p-3 text-right font-semibold text-slate-600">Statement</th>
                </tr>
              </thead>
              <tbody>
                {history.map((d) => (
                  <tr key={d.id} className="border-b border-slate-100">
                    <td className="p-3 text-slate-700">{d.customer_name}</td>
                    <td className="p-3 text-slate-500">{d.cycle_start} → {d.cycle_end}</td>
                    <td className="p-3 text-right text-slate-700">₹{(d.total_amount || 0).toFixed(2)}</td>
                    <td className="p-3">
                      {d.status === "APPROVED" ? (
                        d.bank_transfer_reference ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600" title={`Ref: ${d.bank_transfer_reference}`}>
                            <CheckCircle2 className="w-3.5 h-3.5" /> Paid (Bank Transfer)
                          </span>
                        ) : d.account_mode === "CREDIT" ? (
                          <button
                            onClick={async () => {
                              const ref = window.prompt("Bank reference number (UTR) for this payment:");
                              if (!ref || !ref.trim()) return;
                              try {
                                await api.post(`/v2/monthly-invoices/drafts/${d.id}/mark-paid-bank-transfer`, { reference_number: ref.trim() });
                                toast.success("Marked as paid via bank transfer");
                                fetchDrafts();
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || "Failed to mark paid");
                              }
                            }}
                            className="text-xs font-semibold text-amber-600 hover:underline"
                          >
                            Mark Paid (Bank Transfer)
                          </button>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Approved
                          </span>
                        )
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600" title={d.generation_error || ""}>
                          <XCircle className="w-3.5 h-3.5" /> Generation Failed
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      {d.combined_ref ? (
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/v2/invoices/combined/${d.combined_ref}/export`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-semibold text-brand-600 hover:underline"
                        >
                          <Download className="w-3.5 h-3.5" /> Download
                        </a>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
